import type { MockDatabase, MockRecord } from './database.ts';
import { ADMIN_ID, VENDOR_ID } from './seed.ts';

const id = (group: number, index: number) => `${String(group).padStart(8, '0')}-0000-4000-8000-${String(index + 1).padStart(12, '0')}`;
const stamp = '2026-09-01T10:00:00.000Z';
const migrationId = 'vendor-erd-fixtures-3';
const passwordMigrationId = 'demo-password-reset-1';

/** Every demo account signs in with this; identity falls back to it for accounts created later. */
export const DEMO_PASSWORD = 'Demo123@';
/** What identity.ts expects for the two seeded accounts when no password is stored. */
const LEGACY_PASSWORD = 'Noman123@';

/** The role catalogue is shared, but a company row may only hold these two. */
const COMPANY_ONLY_ROLES = ['Vendor', 'Packager'];

/** VendorsInRoles and VendorUsersInRoles are distinct joins, so an account never holds the other side's roles. */
function separateRoles(record: MockRecord, company: boolean): void {
  const keep = (roleName: string) => COMPANY_ONLY_ROLES.includes(roleName) === company;
  record.roles = ((record.roles as string[] | undefined) ?? []).filter(keep);
  if (Array.isArray(record.roleAssignments)) {
    record.roleAssignments = (record.roleAssignments as MockRecord[]).filter((role) => keep(String(role.roleName)));
  }
}

export const isVendorCompany = (record: MockRecord): boolean => record.portal === 'QCDOC-VENDOR' && record.isVendor === true;
export const isVendorUser = (record: MockRecord): boolean => record.portal === 'QCDOC-VENDOR' && record.isVendor !== true;

/** Vendors columns the qcdoc-api DTOs expose next to the membership account, with their SQL-cased aliases. */
function vendorColumns(vendor: MockRecord, index: number, pslId: string | null): Record<string, unknown> {
  const email = String(vendor.email ?? '');
  const secondaryEmail = email ? `ap.${email}` : '';
  return {
    venSAP: `V-${200100 + index}`, venSap: `V-${200100 + index}`,
    venEmail: email, VenEmail: email,
    secondVenEmail: secondaryEmail, secondaryEmail, secondEmail: secondaryEmail,
    secondFax: '555-0181', secondaryFax: '555-0181',
    secondPhone: '555-0180', secondaryPhone: '555-0180',
    secondMobile: '555-0182', secondaryMobile: '555-0182',
    fax: '555-0101', mobile: '555-0102',
    website: `https://supplier${index + 1}.example.test`,
    pslId, PSLID: pslId, pslName: vendor.psl ?? '',
    approvalComment: 'Synthetic supplier approved for prototype workflows.',
    notes: 'Seeded supplier record for internal vendor administration.',
    createDate: stamp, createdDate: vendor.creationDate ?? stamp,
    lastUpdateDate: stamp, lastUpdatedDate: stamp, lastUpdatedBy: 'demo.user@example.test',
  };
}

/** Additional supplier states the fifty uniform seed rows do not represent. */
function scenarioAccounts(psl: MockRecord | undefined, parent: MockRecord | undefined): MockRecord[] {
  const pslId = psl ? String(psl.oid) : null;
  const pending: MockRecord = {
    oid: id(120, 0), userId: id(120, 0), vendorId: id(120, 0), providerUserKey: id(120, 0),
    userName: 'pending.supplier@example.test', email: 'pending.supplier@example.test',
    name: 'Pending Supply Partner', company: 'Pending Supply Partner', vendorName: 'Pending Supply Partner',
    firstName: 'Pending', lastName: 'Supplier', fullName: 'Pending Supplier',
    portal: 'QCDOC-VENDOR', isInternal: false, isVendor: true,
    isApproved: false, isActive: false, isLockedOut: false,
    psl: psl?.name ?? 'Baroid', roles: ['Vendor'], packaging: { read: true, write: false, admin: false },
    countryId: 1, stateId: 1, address: '900 Pending Way', city: 'Houston', zip: '77032', phone: '555-0190',
    creationDate: stamp, lastLoginDate: '',
    venSAP: 'V-200900', venSap: 'V-200900',
    venEmail: 'pending.supplier@example.test', VenEmail: 'pending.supplier@example.test',
    secondVenEmail: '', secondaryEmail: '', secondFax: '', secondaryFax: '',
    secondPhone: '', secondaryPhone: '', secondMobile: '', secondaryMobile: '',
    fax: '', mobile: '', website: '', pslId, PSLID: pslId, pslName: psl?.name ?? 'Baroid',
    approvalComment: 'Awaiting category manager review before qualification.',
    notes: 'Supplier registration submitted and not yet approved.',
    createDate: stamp, createdDate: stamp, lastUpdateDate: stamp, lastUpdatedDate: stamp,
  };
  if (!parent) return [pending];
  const locked: MockRecord = {
    ...parent, oid: id(121, 0), userId: id(121, 0), providerUserKey: id(121, 0),
    vendorId: parent.oid, isVendor: false, isUser: true,
    userName: 'locked.vendoruser@example.test', email: 'locked.vendoruser@example.test',
    firstName: 'Locked', lastName: 'Vendor User', fullName: 'Locked Vendor User',
    roles: ['Vendor', 'External User'], packaging: { read: true, write: false, admin: false },
    isApproved: true, isActive: false, isLockedOut: true,
    lastLockoutDate: stamp, lastActivityDate: stamp,
    notes: 'Supplier contact locked out after repeated sign-in failures.',
  };
  return [pending, locked];
}

/** VendorsInRoles and VendorUsersInRoles rows projected from the authoritative role assignments. */
export function vendorRoleJoins(database: MockDatabase): { vendorsInRoles: MockRecord[]; vendorUsersInRoles: MockRecord[] } {
  const definitions = new Map((database.roles ?? []).map((role) => [String(role.roleName).toLowerCase(), role]));
  const rows = (record: MockRecord): MockRecord[] => {
    const assigned = Array.isArray(record.roleAssignments) && record.roleAssignments.length
      ? (record.roleAssignments as MockRecord[]).map((role) => String(role.roleName))
      : (record.roles as string[] | undefined) ?? [];
    return assigned.flatMap((roleName) => {
      const definition = definitions.get(roleName.toLowerCase());
      if (!definition || definition.portal !== 'QCDOC-VENDOR') return [];
      const roleId = String(definition.roleId ?? definition.oid);
      return [{ oid: `${record.oid}:${roleId}`, roleId, roleName: String(definition.roleName),
        userId: String(record.userId ?? record.oid), vendorId: String(record.vendorId ?? record.oid), assignedDate: stamp }];
    });
  };
  const users = database.users ?? [];
  return {
    vendorsInRoles: users.filter(isVendorCompany).flatMap(rows),
    vendorUsersInRoles: users.filter(isVendorUser).flatMap(rows),
  };
}

export function syncVendorRoleJoins(database: MockDatabase): void {
  if (!database.vendorsInRoles && !database.vendorUsersInRoles) return;
  const joins = vendorRoleJoins(database);
  database.vendorsInRoles = joins.vendorsInRoles;
  database.vendorUsersInRoles = joins.vendorUsersInRoles;
}

/** The seed pairs every source vendor with fifty products, which reads as noise on the assignment panel. */
function trimPackagerAssignments(database: MockDatabase): void {
  const vendors = (database.packagerVendors ?? []).slice(0, 2);
  const kept = new Set(vendors.map((row) => String(row.vendorId)));
  const used = new Map<string, number>();
  database.packagerProducts = (database.packagerProducts ?? []).filter((row) => {
    const vendorId = String(row.vendorId);
    if (!kept.has(vendorId)) return false;
    const count = used.get(vendorId) ?? 0;
    if (count >= 2) return false;
    used.set(vendorId, count + 1);
    return true;
  });
  for (const vendor of vendors) {
    const assigned = database.packagerProducts.filter((row) => String(row.vendorId) === String(vendor.vendorId));
    vendor.assignedProductNames = assigned.map((row) => String(row.prodName));
    vendor.productCount = assigned.length;
  }
  database.packagerVendors = vendors;
}

/**
 * sp_selPackagerVendor joins the source vendor's profile and aggregates its assigned products, so those
 * columns are derived per read. The insert handler only stores the link row, so recompute them here.
 */
export function reconcilePackagerAssignments(database: MockDatabase): boolean {
  const rows = database.packagerVendors;
  if (!rows?.length) return false;
  const profiles = new Map([...(database.users ?? []), ...(database.vendors ?? [])].map((row) => [String(row.oid), row]));
  let changed = false;
  for (const row of rows) {
    const profile = profiles.get(String(row.vendorId));
    const company = profile?.company ?? profile?.vendorName ?? row.vendorCompany ?? null;
    const packager = profiles.get(String(row.packagerId));
    const assigned = (database.packagerProducts ?? [])
      .filter((product) => String(product.packagerId) === String(row.packagerId) && String(product.vendorId) === String(row.vendorId))
      .map((product) => String(product.prodName));
    const names = row.assignedProductNames;
    if (row.vendorCompany === company && row.productCount === assigned.length &&
      Array.isArray(names) && names.length === assigned.length && names.every((name, index) => name === assigned[index])) continue;
    row.vendorCompany = company;
    row.packagerCompany = packager?.company ?? packager?.vendorName ?? row.packagerCompany ?? null;
    row.productCount = assigned.length;
    row.assignedProductNames = assigned;
    changed = true;
  }
  return changed;
}

/**
 * One-time reset so the seeded admin and vendor accounts stop expecting their own legacy password.
 * Every other account already falls back to the demo password, so only the two legacy ones are
 * stamped — and only while they still hold the old value, so a changed password is never clobbered.
 */
function ensureDemoPasswords(database: MockDatabase): boolean {
  if (database.adminMigrations?.some((row) => row.oid === passwordMigrationId)) return false;
  for (const table of ['users', 'vendors']) {
    for (const record of database[table] ?? []) {
      if (record.oid !== ADMIN_ID && record.oid !== VENDOR_ID) continue;
      if (record.password === undefined || record.password === LEGACY_PASSWORD) record.password = DEMO_PASSWORD;
    }
  }
  (database.adminMigrations ??= []).push({ oid: passwordMigrationId, appliedAt: new Date().toISOString() });
  return true;
}

/** Additive, one-time migration within the existing storage version. Never resets vendors or their users. */
export function ensureVendorFixtures(database: MockDatabase): boolean {
  const passwords = ensureDemoPasswords(database);
  const assignments = reconcilePackagerAssignments(database);
  if (database.adminMigrations?.some((row) => row.oid === migrationId)) return passwords || assignments;
  const psl = (database.psls ?? []).find((row) => row.name === 'Baroid') ?? database.psls?.[0];
  const pslId = psl ? String(psl.oid) : null;
  const companies = (database.users ?? []).filter(isVendorCompany);
  companies.forEach((vendor, index) => {
    for (const [key, value] of Object.entries(vendorColumns(vendor, index, pslId))) {
      if (vendor[key] === undefined) vendor[key] = value;
    }
    separateRoles(vendor, true);
  });
  for (const user of (database.users ?? []).filter(isVendorUser)) {
    const defaults = { secondVenEmail: '', secondaryEmail: '', secondFax: '', secondaryFax: '',
      createDate: stamp, createdDate: user.creationDate ?? stamp, lastUpdateDate: stamp, lastActivityDate: stamp,
      pslId, PSLID: pslId, pslName: user.psl ?? '' };
    for (const [key, value] of Object.entries(defaults)) if (user[key] === undefined) user[key] = value;
    separateRoles(user, false);
  }
  const accounts = scenarioAccounts(psl, companies[0]);
  const users = database.users ??= [];
  for (const account of accounts) {
    if (users.some((existing) => existing.oid === account.oid || String(existing.userName).toLowerCase() === String(account.userName).toLowerCase())) continue;
    separateRoles(account, isVendorCompany(account));
    users.push(account);
    if (isVendorCompany(account)) (database.vendors ??= []).push(account);
  }
  // Mirror the membership rows onto the ERD Vendors table so both stay addressable by VendorId.
  const vendors = database.vendors ??= [];
  for (const vendor of (database.users ?? []).filter(isVendorCompany)) {
    const existing = vendors.find((row) => row.oid === vendor.oid);
    if (existing) Object.assign(existing, vendor);
  }
  const joins = vendorRoleJoins(database);
  database.vendorsInRoles = joins.vendorsInRoles;
  database.vendorUsersInRoles = joins.vendorUsersInRoles;
  trimPackagerAssignments(database);
  (database.adminMigrations ??= []).push({ oid: migrationId, appliedAt: new Date().toISOString() });
  return true;
}
